import { Component, Input, OnInit, Output, EventEmitter, ViewChild, AfterViewInit, ElementRef } from '@angular/core';
import {
  BsDaterangepickerDirective,
  BsDaterangepickerContainerComponent,
  ɵk as BsDatepickerNavigationViewComponent,
  ɵl as BsDaysCalendarViewComponent,
} from 'ngx-bootstrap/datepicker';
import { CellHoverEvent, DayViewModel, DatepickerDateCustomClasses } from 'ngx-bootstrap/datepicker/models';
import moment from 'moment';
import { IDatepickerConfig, IDatePeriod } from '../types';
import { ITabItem } from '../types/picker.type';
import { DateGranularity } from '../datepicker-specific-constants';
import { detectMobile } from '@xenial-ux/utils';
import { ComponentStaticText } from '../components';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'xux-datepicker-range',
  templateUrl: './xux-datepicker-range.component.html',
  styleUrls: ['./xux-datepicker-range.component.scss'],
  providers: [
    BsDaterangepickerDirective,
    BsDaysCalendarViewComponent,
    BsDaterangepickerContainerComponent,
    BsDatepickerNavigationViewComponent,
  ],
})
export class XuxDatepickerRangeComponent implements OnInit, AfterViewInit {
  @Input() public config: IDatepickerConfig;
  @Input() set calendarValue(_value: IDatePeriod) {
    let value: IDatePeriod = _value || {};
    if (this.config && !this.config.clearInvalidDateSelection && this.config.minDate) {
      value.dateStart = (value.dateStart < this.config.minDate) ? this.config.minDate : value.dateStart;
      value.dateEnd = (value.dateEnd < this.config.minDate) ? this.config.minDate : value.dateEnd;
    }
    if (this.config && !this.config.clearInvalidDateSelection && this.config.maxDate) {
      value.dateStart = (value.dateStart > this.config.maxDate) ? this.config.maxDate : value.dateStart;
      value.dateEnd = (value.dateEnd > this.config.maxDate) ? this.config.maxDate : value.dateEnd;
    }

    if (this.config && this.config.clearInvalidDateSelection) {
      value = this.getValidatedValue(value, this.config.minDate, this.config.maxDate);
    }
    this._bsValue = [
      value.dateStart,
      value.dateEnd,
    ];
    if (this.initialized) {
      this.setValues(value);
    }
  }
  @Input() public set i18n(staticTextContent: ComponentStaticText) {
    this.staticTextContent = {
      ...this.staticTextContent,
      ...(staticTextContent || {}),
    };
  }

  public staticTextContent: ComponentStaticText = {
    selectFrom: 'Select From',
    selectTo: 'Select To',
    from: this.translations.instant('DATEPICKER.FROM'),
    to: this.translations.instant('DATEPICKER.TO'),
  };
  public dpConfig: any = {
    containerClass: 'theme-dark-blue',
    showWeekNumbers: false,
    displayMonths: 1,
    selectFromOtherMonth: true,
  };
  public tabValues: ITabItem = {
    from: {},
    to: {},
  };
  public activeTab: 'from' | 'to' = 'from';
  public _bsValue: (Date | string)[];
  public showDropdown:  { year: boolean, month: boolean } = {
    year: false,
    month: false,
  };
  public DateGranularity: typeof  DateGranularity = DateGranularity;
  public minDate: Date;
  public dayTooltipTitle: string = '';
  public customClasses: DatepickerDateCustomClasses[];

  public months: { number: number, name: string }[];
  public years: number[] = [];
  @Output() public onChange: EventEmitter<IDatePeriod> = new EventEmitter();
  @Output() public onAnyChange: EventEmitter<IDatePeriod> = new EventEmitter();
  @ViewChild('monthSelector', { static: false }) private monthSelector: ElementRef;
  @ViewChild('yearsSelector', { static: false }) private yearsSelector: ElementRef;
  @ViewChild('dayTooltipContainer', { static: false }) private dayTooltipContainer: ElementRef;
  private values: IDatePeriod = { dateStart: null, dateEnd: null };
  private initialized: boolean = false;
  private internalDpMethods: { monthSelectHandler: Function, yearSelectHandler: Function} = {
    monthSelectHandler: null,
    yearSelectHandler: null,
  };

  constructor(
    private daterangepickerDirective: BsDaterangepickerDirective,
    private bsDaterangepickerContainerComponent: BsDaterangepickerContainerComponent,
    private bsDatepickerNavigationViewComponent: BsDatepickerNavigationViewComponent,
    private elementRef: ElementRef,
    private translations: TranslateService,
  ) {
    this.months = moment.monthsShort()
      .map((monthName, index) => ({ number: index, name: monthName }));
  }

  public ngOnInit(): void {
    this.initialized = true;
    this.dpConfig.locale = this.config.locale;
    const minYear = this.config.yearsRange[0];
    const maxYear = this.config.yearsRange[1];
    for (let i = maxYear; i >= minYear; i -= 1) {
      this.years.push(i);
    }
    this.minDate = this.config.minDate;

    if (this._bsValue) {
      this.setValues({ dateStart: this._bsValue[0], dateEnd: this._bsValue[1] });
    }

    this.replaceDpMethods();
  }

  public ngAfterViewInit(): void {
    this.insertCustomDropdowns();
  }

  public showMonth(month: number): void {
    if (
        (this.config.maxDate?.getMonth() !== month || this.minDate?.getMonth() !== month)
        && this.isDateDisabled(DateGranularity.MONTH, month)
    ) { return; }

    const currentYear = moment(this.tabValues[this.activeTab].viewDate).get(DateGranularity.YEAR);
    const monthDate = moment.isDate(month) ? month : moment([currentYear, month]).toDate();
    const event = { date: monthDate, label: '' };
    this.tabValues[this.activeTab].viewDate = monthDate;
    this.internalDpMethods.monthSelectHandler(event);
    this.showDropdown.month = false;
    this.showDropdown.year = false;
    this.insertCustomDropdowns();
  }

  public showYear(year: number, month?: number): void {
    const newDate = moment(this.tabValues[this.activeTab].viewDate).set(DateGranularity.YEAR, year).toDate();
    const event = { date: newDate, label: '' };
    this.tabValues[this.activeTab].viewDate = newDate;
    this.internalDpMethods.yearSelectHandler(event);

    let currentMonth = this.tabValues[this.activeTab].viewDate.getMonth();
    if (month !== undefined) {
      currentMonth = month;
    }

    this.showMonth(currentMonth);
    this.insertCustomDropdowns();
  }

  public isDateDisabled(unit: DateGranularity.MONTH | DateGranularity.YEAR, value: number): boolean {
    const newValue = moment(this.tabValues[this.activeTab].viewDate).set(unit, value);

    const isBeforeMin = !!this.minDate && newValue.isBefore(this.minDate, unit);
    const isAfterMax = !!this.config.maxDate && newValue.isAfter(this.config.maxDate, unit);
    return isBeforeMin || isAfterMax;
  }

  public onValueChange(newValues: Date[]): void {
    const dateStart = newValues[0];
    const dateEnd = newValues[1];

    this.setValues({ dateStart, dateEnd });
    this.onChange.next(this.values);
  }

  public isEditable(): boolean {
    return !!this.config.availableOptions
        .filter(option => option.id === 'custom')
        .length;
  }

  public setTab(tabId: 'from' | 'to', year?: number, month?: number): void {
    this.activeTab = tabId;
    if (year !== undefined && month !== undefined) {
      this.showYear(year, month);
    }

    this.insertCustomDropdowns();
  }

  private insertCustomDropdowns(): void {
    this.showDropdown.month = false;
    this.showDropdown.year = false;

    setTimeout(() => {
      const monthsContainer = this.elementRef.nativeElement
          .querySelector('bs-datepicker-navigation-view .current');
      const yearsContainer = this.elementRef.nativeElement
          .querySelector('bs-datepicker-navigation-view .current + .current');
      if (monthsContainer) {
        monthsContainer
            .appendChild(this.monthSelector.nativeElement);
      }
      if (yearsContainer) {
        yearsContainer
            .appendChild(this.yearsSelector.nativeElement);
      }
    }, 10);
  }

  private getDates(startDate: Date | string, endDate: Date | string): Date[] {
    const dateArray = [];
    let currentDate = moment(startDate);
    const stopDate = moment(endDate);
    while (currentDate <= stopDate) {
      dateArray.push(currentDate.toDate());
      currentDate = moment(currentDate).add(1, 'days');
    }
    return dateArray;
  }

  private setValues(newValues: IDatePeriod): void {
    this.values = newValues;
    const format = this.config.dateFormat.long_date_format;

    if (this.values.dateStart) {
      const startDate = moment(this.values.dateStart);
      this.tabValues.from.date = startDate.format(format);
      this.tabValues.from.viewDate = this.values.dateStart instanceof Date ? this.values.dateStart : startDate.toDate();
    }
    if (this.values.dateEnd) {
      const dateEnd = moment(this.values.dateEnd);
      this.tabValues.to.date = dateEnd.format(format);
      this.tabValues.to.viewDate = this.values.dateEnd instanceof Date ? this.values.dateEnd : dateEnd.toDate();
    }
    this.onAnyChange.next(this.values);
    if (this.values.dateEnd) {
      this.customClasses = this.getDates(this.values.dateStart, this.values.dateEnd)
          .map((date) => {
            return { date, classes: ['in-selected-range'] };
          });
    }
    this.insertCustomDropdowns();
  }

  private replaceDpMethods(): void {
    const self = this;

    // workaround until datepicker has inline mode.
    // .hide() method is rewritten to keep calendar opened & working when dates are selected.
    const replaceHideMethod = (): void => {
      (<any>this.daterangepickerDirective).__proto__.hide = (): void => {};
    };

    const replaceViewMethod = (): void => {
      type YearOrMonth = DateGranularity.YEAR | DateGranularity.MONTH;
      (<any>this.bsDatepickerNavigationViewComponent).__proto__.view = (name: YearOrMonth): void => {
        if (!this.isEditable()) {
          return;
        }

        if (name === DateGranularity.YEAR) {
          this.showDropdown[DateGranularity.MONTH] = false;
        }

        if (name === DateGranularity.MONTH) {
          this.showDropdown[DateGranularity.YEAR] = false;
        }

        this.showDropdown[name] = !this.showDropdown[name];
      };
    };

    const replaceDaySelectHandler = (): Function => {
      return function (this: any, day: DayViewModel): void {
        let currentMonth = day.date.getMonth();
        let currentYear = day.date.getFullYear();
        if (day.isOtherMonth) {
          if (Number(day.label) <= 15) {
            currentMonth -= 1;
            if (currentMonth < 0) {
              currentMonth = 11;
              currentYear -= 1;
            }
          } else {
            currentMonth += 1;
            if (currentMonth > 11) {
              currentMonth = 0;
              currentYear += 1;
            }
          }
        }
        if (day.isDisabled) {
          return;
        }

        switch (self.activeTab) {
          case 'from': {
            self.customClasses = [];
            self.setValues({ dateStart: day.date });
            self.setTab('to', currentYear, currentMonth);

            this._rangeStack = [];
            this.__proto__.daySelectHandler.call(this, day);
            self.showMonth(currentMonth);
            break;
          }
          case 'to': {
            if (day.date < self.values.dateStart) { return; }

            if (!self.values.dateEnd) {
              this.__proto__.daySelectHandler.call(this, day);
            } else {
              const newValue = day.date >= self.values.dateStart ? day.date : self.values.dateStart;
              self.values.dateEnd = newValue;

              this._rangeStack = [self.values.dateStart];
              this.__proto__.daySelectHandler.call(this, day);
            }
            self.setTab('from', currentYear, currentMonth);
            break;
          }
          default:
        }
      };
    };
    // Since we are unable to get access to inner bootstrap components or directives
    // used decorator for dayHoverHandler method
    // in order to get access to day cell data needed for tooltip
    const replaceOnDayHoverMethod =
      (bsDaterangepickerContainerComponent: BsDaterangepickerContainerComponent): void => {
        const originalOnHover = bsDaterangepickerContainerComponent.dayHoverHandler;

        bsDaterangepickerContainerComponent.dayHoverHandler = function (event: CellHoverEvent): void {
          // Not apply hover for touch devices
          if (!detectMobile()) {
            originalOnHover.call(this, event);
            self.showDayDisabledTooltip(event);
            self.setRangeHoverState(event);
          }
        };
      };

    const replaceNgOnInit = (): void => {
      const originalNgOnInit = (<any>this.bsDaterangepickerContainerComponent).__proto__.ngOnInit;
      (<any>this.bsDaterangepickerContainerComponent).__proto__.ngOnInit = function (): void {
        originalNgOnInit.call(this);
        self.internalDpMethods.monthSelectHandler = this.monthSelectHandler;
        self.internalDpMethods.yearSelectHandler = this.yearSelectHandler;

        // decorate daterangepicker method to handle each change
        this.daySelectHandler = replaceDaySelectHandler();

        replaceOnDayHoverMethod(this);
      };
    };

    const replaceNavToMethod = (): void => {
      const originalNavTo = (<any>this.bsDatepickerNavigationViewComponent).__proto__.navTo;
      (<any>this.bsDatepickerNavigationViewComponent).__proto__.navTo = function (down: boolean): void {
        const currentDate = self.tabValues[self.activeTab].viewDate;
        const value = down ? -1 : 1;
        const newDate = moment(currentDate).add(value, DateGranularity.MONTH);
        self.tabValues[self.activeTab].viewDate = newDate.toDate();
        originalNavTo.call(this, down);
        self.insertCustomDropdowns();
      };
    };

    replaceHideMethod();
    replaceViewMethod();
    replaceNavToMethod();
    replaceNgOnInit();
  }

  private getValidatedValue(valueToBeValidated: IDatePeriod, minDate: Date, maxDate: Date): IDatePeriod {
    const { dateStart, dateEnd } = valueToBeValidated;
    const dateRangeWithoutValues: IDatePeriod = { dateStart: null, dateEnd: null };

    if (!dateStart || !dateEnd) {
      return dateRangeWithoutValues;
    }

    if (minDate) {
      if (
        moment(dateStart) < moment(minDate).startOf(DateGranularity.DAY) ||
        moment(dateEnd) < moment(minDate).startOf(DateGranularity.DAY)
      ) {
        return dateRangeWithoutValues;
      }
    }

    if (maxDate) {
      if (
        moment(dateStart) > moment(maxDate).endOf(DateGranularity.DAY) ||
        moment(dateEnd) > moment(maxDate).endOf(DateGranularity.DAY)
      ) {
        return dateRangeWithoutValues;
      }
    }

    if (
      moment(dateEnd).endOf(DateGranularity.DAY) <
      moment(dateStart).startOf(DateGranularity.DAY)
    ) {
      return dateRangeWithoutValues;
    }

    return valueToBeValidated;
  }

  private setRangeHoverState(event: { cell: DayViewModel, isHovered: boolean }): void {
    if ((this.values.dateStart && !this.values.dateEnd) && event.cell.date > this.values.dateStart) {
      this.customClasses = this.getDates(this.values.dateStart, event.cell.date)
          .map((date) => {
            return { date, classes: ['hovered'] };
          });
    }
  }

  private showDayDisabledTooltip(event: { cell: DayViewModel, isHovered: boolean }): void {
    if (!event || !event.cell || !event.cell.isDisabled || !this.config || !this.config.dayTooltipTitles) {
      return;
    }

    const { cell : { date, weekIndex, dayIndex }, isHovered } = event;
    const { minDate, maxDate, dayTooltipTitles, disabledDates } = this.config;

    const isLessThanMin: boolean = minDate && dayTooltipTitles.disabledMinDate && moment(date) < moment(minDate);
    const isGreaterThanMax: boolean = maxDate && dayTooltipTitles.disabledMaxDate && moment(date) > moment(maxDate);
    const isDisabledDate: boolean = disabledDates && dayTooltipTitles.disabledDates
        && !!disabledDates.filter(disableDate => moment(disableDate).isSame(moment(date),  DateGranularity.DAY)).length;

    const tooltipElement = this.dayTooltipContainer.nativeElement;

    if (isLessThanMin) {
      this.dayTooltipTitle = dayTooltipTitles.disabledMinDate;
    }

    if (isGreaterThanMax) {
      this.dayTooltipTitle = dayTooltipTitles.disabledMaxDate;
    }

    if (isDisabledDate) {
      this.dayTooltipTitle = dayTooltipTitles.disabledDates;
    }

    if (!this.dayTooltipTitle) {
      return;
    }

    const disabledDaySelector =
      `.days.weeks tbody tr:nth-child(${weekIndex + 1}) td:nth-child(${dayIndex + 1}) span.disabled`;

    const targetElement: HTMLElement = this.elementRef.nativeElement
      .querySelector(disabledDaySelector);

    if (!targetElement) {
      return;
    }

    if (isHovered) {
      targetElement.appendChild(tooltipElement);
    } else {
      this.dayTooltipTitle = '';
      this.elementRef.nativeElement.append(tooltipElement);
    }
  }
}
